import { ApiError } from '@/services/api-client';

export interface IChangeEmailErrorDescription {
    /**
     * `info` - the change did not happen because the address was already what
     * the user asked for; the caller refreshes the token and moves on.
     * `inline` - a refusal the user can act on, shown next to the field.
     */
    kind: 'info' | 'inline';
    message: string;
}

/**
 * Turns a `changeEmail` rejection into copy, once. Shared by the payment
 * page's email card and the account banner / clinical dialog of the
 * onboarding profile work (ESCLOUD-333), so the wording for each refusal code
 * (ESCLOUD-331) lives in a single place instead of drifting between the two
 * surfaces.
 */
export function describeChangeEmailError(error: unknown): IChangeEmailErrorDescription {
    if (error instanceof ApiError) {
        if (error.errorCode === 'email_already_verified') {
            return { kind: 'info', message: 'Your address is already verified' };
        }
        if (error.errorCode === 'email_in_use') {
            return { kind: 'inline', message: 'This address cannot be used.' };
        }
        if (error.errorCode === 'email_change_unsupported') {
            return { kind: 'inline', message: 'This address cannot be changed here.' };
        }
        if (error.status === 429) {
            return { kind: 'inline', message: 'Too many attempts. Try again in an hour.' };
        }
    }
    return { kind: 'inline', message: 'The address could not be changed. Please try again.' };
}

/**
 * The codes with dedicated copy above, plus the two other refusals ESCLOUD-331
 * expects in normal operation (a busy change service, a rate limit). Used to
 * decide what is worth logging: an expected refusal is shown to the user and
 * left alone, anything else is a bug worth a log entry.
 */
export const CHANGE_EMAIL_ERROR_CODES = [
    'email_already_verified',
    'email_in_use',
    'email_change_unsupported',
    'email_change_unavailable',
    'verification_email_rate_limited',
] as const;

/** Whether a `changeEmail` rejection is one ESCLOUD-331 expects, not a bug. */
export function isExpectedChangeEmailRefusal(error: unknown): boolean {
    if (!(error instanceof ApiError)) return false;
    if (error.status === 429) return true;
    return !!error.errorCode && (CHANGE_EMAIL_ERROR_CODES as readonly string[]).includes(error.errorCode);
}
